import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { businessQueryOptions, metaIntegrationStatusQueryOptions } from '@/lib/query-options';
import { useTestMode } from '@/hooks/useTestMode';
import { useStore } from 'jotai';
import { useAtomValue } from 'jotai';
import { LogOut, UserCircle, Plug, BookOpen, ExternalLink, Building2, ChevronRight, ChevronDown, type LucideIcon } from 'lucide-react';
import { AttentionBadge } from '@/components/glitch-ui/attention-badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useUserProfile } from '@/contexts/user.context';
import { GoogleAdAccountSelectionModal } from '../glitch-ui/google-ad-account-selection-modal';
import { MetaAdAccountSelectionModal } from '../glitch-ui/meta-ad-account-selection-modal';
import { OrganizationSelectionModal } from '../navigation-sidebar/organization-selection-modal';
import { clearAllUserData } from '@/lib/auth-cleanup';
import { defaultProfile, deleteAccessTokenCookie } from '@/lib/auth';
import { organizationDataAtom, googleAdsAccountAtom } from '@/store';
import { useOrganizationInitialization } from '@/hooks/useOrganizationInitialization';
import { usePaymentGate } from '@/hooks/usePaymentGate';
import { PlanType, type OrganizationDataWithUsersMembership } from '@/lib/types';
import googleAds from '/google-ads.svg';
import metaLogo from '/meta.png';

export function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMetaModalOpen, setIsMetaModalOpen] = useState(false);
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  // "Ad Accounts >" disclosure state. Test-mode-only group that wraps the Google + Meta picker
  // entries — production users see today's flat "Google Ads Account" item unchanged (#4173 Chunk 7).
  // Expanded by default so both ad-account entries are visible without an extra click (#3817).
  const [isAdAccountsExpanded, setIsAdAccountsExpanded] = useState(true);
  const { user, setUser } = useUserProfile();
  const { organizations, organizationData, switchOrganization } = useOrganizationInitialization();
  const organization = useAtomValue(organizationDataAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const store = useStore();
  // Meta UX is pre-GA per [[feedback_frontend_gating_pattern]] — the "Meta Ads Account" menu entry
  // Read Meta integration state from server-backed React Query (the same source the modal uses) so the
  // "Ad Accounts → Meta" label stays in sync with reality on cold loads. A prior implementation read a
  // Jotai atom written only by the OAuth callback and the integration-modal, so it was null for users
  // who connected in an earlier session and reloaded, surfacing a stale "Not connected".
  const isTestMode = useTestMode();
  const { data: metaIntegration } = useQuery(metaIntegrationStatusQueryOptions());

  const { PaymentGate, requirePayment } = usePaymentGate({ source: 'user_menu_upgrade' });

  const isFreeUser = organization?.plan_type === PlanType.FREE;
  const { data: business } = useQuery({
    ...businessQueryOptions(organization?.id || 0),
    enabled: !!organization?.id,
  });
  const isCurrencyMissing = !!business && !organization?.default_currency;

  const logout = () => {
    deleteAccessTokenCookie();
    setUser(defaultProfile);
    clearAllUserData(queryClient, store);
    navigate({ to: '/signin' });
  };

  const handleGoogleAdAccountSelection = () => {
    setIsOpen(false);
    setIsModalOpen(true);
  };

  const handleMetaAdAccountSelection = () => {
    setIsOpen(false);
    setIsMetaModalOpen(true);
  };

  const handleOrgModalClose = () => {
    setIsOrgModalOpen(false);
  };

  const handleOrganizationSelect = async (organization: OrganizationDataWithUsersMembership) => {
    await switchOrganization(organization);
  };

  const handleUpgradeClick = () => {
    requirePayment(() => {});
  };

  const handleNavigate = (to: string) => {
    setIsOpen(false);
    navigate({ to });
  };

  // Top-of-menu items (Account / Organizations / Integrations). The ad-account picker entries are
  // rendered separately below so we can group them under "Ad Accounts >" in test mode (#4173 Chunk 7).
  // The avatar's missing-currency alert repeats on Organizations, where the currency is set, so the
  // opened menu says where to go rather than just that something is wrong.
  const menuItems: { icon: LucideIcon; label: string; onClick: () => void; attention?: boolean }[] = [
    { icon: UserCircle, label: 'Account', onClick: () => handleNavigate('/settings/account') },
    { icon: Building2, label: 'Organizations', onClick: () => handleNavigate('/organizations'), attention: isCurrencyMissing },
    // The standalone /settings/integrations page is retired (#6372); the one
    // Integrations surface is the Account page's sub-tab.
    { icon: Plug, label: 'Integrations', onClick: () => { setIsOpen(false); navigate({ to: '/settings/account', hash: 'integrations' }); } },
  ];

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button data-testid="user-menu-trigger" className="flex w-full flex-col items-center gap-1 rounded-lg px-1 py-2 text-gray-900 hover:bg-gray-50 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#290BD9] focus-visible:ring-inset">
            <span className="relative">
              <Avatar className="h-6 w-6 rounded-full">
                <AvatarImage src={user.picture} alt={user.name} />
                <AvatarFallback className="rounded-full bg-[#290BD9] bg-opacity-10 text-[#290BD9] text-[10px]">
                  {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </AvatarFallback>
              </Avatar>
              {isCurrencyMissing && (
                <AttentionBadge className="absolute -top-1 -right-1.5" />
              )}
            </span>
            <span className="max-w-full truncate text-[10px] font-medium leading-none">Account</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="end"
          sideOffset={12}
          className="w-80 p-0 rounded-xl shadow-lg border border-gray-200"
        >
          {/* User info */}
          <div className="p-4 pb-3">
            <div className="flex items-start gap-3">
              <Avatar className="h-10 w-10 rounded-full shrink-0 self-center">
                <AvatarImage src={user.picture} alt={user.name} />
                <AvatarFallback className="rounded-full bg-[#290BD9] bg-opacity-10 text-[#290BD9]">
                  {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                {user.impersonating_email && (
                  <>
                    <p className="text-xs text-yellow-600 font-medium">Impersonating:</p>
                    <p className="text-xs text-yellow-600 truncate">{user.impersonating_email}</p>
                  </>
                )}
                <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                <p className="text-xs text-gray-500">
                  {organizationData?.name}{' '}
                  {isFreeUser ? (
                    <span onClick={handleUpgradeClick} className="cursor-pointer hover:opacity-80 text-[#B91C1C]">
                      (FREE)
                    </span>
                  ) : (
                    organizationData?.plan_type ? <span>({organizationData.plan_type})</span> : null
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div className="border-t border-gray-100 py-1">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={item.onClick}
                className="w-full flex items-center gap-3 px-4 h-[44px] text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {item.icon ? <item.icon className="h-4 w-4 text-gray-400" /> : null}
                {item.label}
                {item.attention ? <AttentionBadge className="ml-auto" /> : null}
              </button>
            ))}

            {/* Ad-account picker entries: Google + Meta inside an expandable "Ad Accounts >" group
                (#4173 Chunk 7), for everyone now that Meta is out of test mode (#6678). */}
            <button
              onClick={() => setIsAdAccountsExpanded((v) => !v)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              aria-expanded={isAdAccountsExpanded}
            >
              {isAdAccountsExpanded
                ? <ChevronDown className="h-4 w-4 text-gray-400" />
                : <ChevronRight className="h-4 w-4 text-gray-400" />}
              Ad Accounts
            </button>
            {isAdAccountsExpanded && (
              <>
                <button
                  onClick={handleGoogleAdAccountSelection}
                  className="w-full flex items-center gap-3 pl-11 pr-4 h-[44px] text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <img src={googleAds} alt="" className="h-4 w-4 shrink-0" />
                  <span className="flex flex-col items-start min-w-0">
                    <span>Google</span>
                    <span className="text-xs text-gray-500 truncate max-w-[180px]">{googleAdsAccount?.name || 'Not connected'}</span>
                  </span>
                </button>
                <button
                  onClick={handleMetaAdAccountSelection}
                  className="w-full flex items-center gap-3 pl-11 pr-4 h-[44px] text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {/*
                    Meta SVG has more internal whitespace than google-ads.svg, so at the same h-4 w-4
                    box the visible glyph looks ~half the size of Google's. Bumping to h-5 w-5 brings
                    the rendered glyph to roughly the same optical weight (#4173 testing feedback).
                  */}
                  <img src={metaLogo} alt="" className="w-5 h-auto shrink-0" />
                  <span className="flex flex-col items-start min-w-0">
                    <span>Meta</span>
                    <span className="text-xs text-gray-500 truncate max-w-[180px]">{metaIntegration?.selected_ad_account?.name || 'Not connected'}</span>
                  </span>
                </button>
              </>
            )}

            <a
              href="https://glitchads.ai/knowledge-center/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <BookOpen className="h-4 w-4 text-gray-400" />
              Knowledge Center
              <ExternalLink className="ml-auto h-3 w-3 text-gray-300" />
            </a>
          </div>

          {/* Sign out */}
          <div className="border-t border-gray-100 p-3">
            <button
              data-testid="user-menu-logout"
              onClick={logout}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <GoogleAdAccountSelectionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      {isTestMode && (
        <MetaAdAccountSelectionModal isOpen={isMetaModalOpen} onClose={() => setIsMetaModalOpen(false)} />
      )}
      {organizations && (
        <OrganizationSelectionModal
          isOpen={isOrgModalOpen}
          onClose={handleOrgModalClose}
          organizations={organizations}
          selectedOrganization={organizationData || null}
          onOrganizationSelect={handleOrganizationSelect}
        />
      )}

      <PaymentGate />
    </>
  );
}
